import { ulid } from "ulid";
import {
  CartRecord,
  CartLegacy,
  CartItemLegacy,
  CheckoutDeliveryAddress,
  OrderDispatchOption,
  TimeSlot,
} from "~/types";
import { ThunkAction } from "../thunk";
import { Ordering } from "./OrderingService";
import { getIsLoading } from "./reducer";

interface OrderingSetRef {
  type: "ORDERING_SET_REF";
  ref: string;
  locationRef: string;
}

interface OrderingSetNotes {
  type: "ORDERING_SET_NOTES";
  notes: string;
  locationRef: string;
}

interface OrderingServiceRef {
  type: "ORDERING_SET_SERVICEREF";
  serviceRef: string;
  locationRef: string;
}
interface OrderingDeliveryAddress {
  type: "ORDERING_SET_DELIVERY_ADDRESS";
  deliveryAddress: CheckoutDeliveryAddress | null;
  locationRef: string;
}

interface OrderingAddItem {
  type: "ORDERING_ADD_ITEM";
  item: CartItemLegacy;
  locationRef: string;
}

interface OrderingUpdateItem {
  type: "ORDERING_UPDATE_ITEM";
  item: CartItemLegacy;
  locationRef: string;
}

interface OrderingRemoveItem {
  type: "ORDERING_REMOVE_ITEM";
  cartItemId: string;
  locationRef: string;
}

interface FetchOrderingConfig {
  type: "FETCH_ORDERING_CONFIG";
  locationRef: string;
}

interface FetchOrderingConfigSuccess {
  type: "FETCH_ORDERING_CONFIG_SUCCESS";
  locationRef: string;
  ordering: Ordering | null;
}

interface FetchOrderingConfigError {
  type: "FETCH_ORDERING_CONFIG_ERROR";
  locationRef: string;
  error: string;
}

interface OrderingClearCart {
  type: "ORDERING_CLEAR_CART";
  locationRef: string;
}

interface OrderingSetCart {
  type: "ORDERING_SET_CART";
  locationRef: string;
  cart: CartLegacy;
}

interface SetOrderDispatchOption {
  type: "SET_ORDER_DISPATCH_OPTION";
  dispatchOption: OrderDispatchOption;
}

interface ApplyServiceCharge {
  type: "APPLY_SERVICE_CHARGE";
}

interface RemoveServiceCharge {
  type: "REMOVE_SERVICE_CHARGE";
}
interface SetVoucherCode {
  type: "SET_VOUCHERCODE";
  voucherCode: string;
}

interface SetDispatchTime {
  type: "SET_DISPATCH_TIME";
  timeSlot: TimeSlot | null;
}

export type OrderingAction =
  | OrderingSetRef
  | OrderingAddItem
  | OrderingUpdateItem
  | OrderingRemoveItem
  | OrderingSetNotes
  | OrderingServiceRef
  | OrderingDeliveryAddress
  | FetchOrderingConfig
  | FetchOrderingConfigSuccess
  | FetchOrderingConfigError
  | OrderingClearCart
  | SetOrderDispatchOption
  | RemoveServiceCharge
  | ApplyServiceCharge
  | SetDispatchTime
  | SetVoucherCode
  | OrderingSetCart;

export function clearOrderingCart(locationRef: string): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "ORDERING_CLEAR_CART",
      locationRef,
    });
  };
}

export function fetchOrderingConfig(locationRef: string): ThunkAction {
  return async (dispatch, getState, { getOrderService }) => {
    const loading = getIsLoading(getState().ordering, locationRef);

    if (loading) {
      return;
    }

    dispatch({
      type: "FETCH_ORDERING_CONFIG",
      locationRef,
    });

    try {
      const ordering = await getOrderService(getState(), dispatch).fetch(
        locationRef,
        getState().ordering.orderingByLocation[locationRef].deliveryAddress
          ?.address?.coords,
      );

      dispatch({
        type: "FETCH_ORDERING_CONFIG_SUCCESS",
        locationRef,
        ordering,
      });
    } catch (err) {
      const e = err as Error;
      dispatch({
        type: "FETCH_ORDERING_CONFIG_ERROR",
        locationRef,
        error: e.message,
      });
    }
  };
}

export function setOrderingNotes(
  notes: string,
  locationRef: string,
): ThunkAction {
  return async (dispatch) => {
    dispatch({ type: "ORDERING_SET_NOTES", notes, locationRef });
  };
}

export function setOrderingServiceRef(
  serviceRef: string,
  locationRef: string,
): ThunkAction {
  return async (dispatch) => {
    dispatch({ type: "ORDERING_SET_SERVICEREF", serviceRef, locationRef });
  };
}

export function setVoucherCode(voucherCode: string): ThunkAction {
  return async (dispatch) => {
    dispatch({ type: "SET_VOUCHERCODE", voucherCode });
  };
}

export function setOrderingRef(ref: string, locationRef: string): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "ORDERING_SET_REF",
      ref,
      locationRef,
    });
  };
}

export function setOrderingDeliveryAddress(
  deliveryAddress: CheckoutDeliveryAddress | null,
  locationRef: string,
): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "ORDERING_SET_DELIVERY_ADDRESS",
      deliveryAddress,
      locationRef,
    });
    if (deliveryAddress?.address?.coords) {
      dispatch(fetchOrderingConfig(locationRef));
    }
  };
}

export function updateCartItem(
  cartItem: CartItemLegacy,
  locationRef: string,
): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "ORDERING_UPDATE_ITEM",
      item: cartItem,
      locationRef,
    });
  };
}

export function setCart(cart: CartRecord, locationRef: string): ThunkAction {
  return async (dispatch) => {
    const cartItems: CartItemLegacy[] = cart.items.map((cartItem) => ({
      id: ulid(),
      productId: cartItem.id,
      note: cartItem.note ?? "",
      quantity: cartItem.quantity,
      options: cartItem.options,
    }));
    dispatch({
      type: "ORDERING_SET_CART",
      cart: {
        items: cartItems,
        notes: cart?.notes ?? "",
      },
      locationRef,
    });
  };
}

export function addCartItem(
  item: CartItemLegacy,
  locationRef: string,
): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "ORDERING_ADD_ITEM",
      item,
      locationRef,
    });
  };
}

export function removeCartItem(
  cartItemId: string,
  locationRef: string,
): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "ORDERING_REMOVE_ITEM",
      cartItemId,
      locationRef,
    });
  };
}

export function setOrderDispatchOption(
  dispatchOption: OrderDispatchOption,
): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "SET_ORDER_DISPATCH_OPTION",
      dispatchOption,
    });
    dispatch(setOrderDispatchTime(null));
  };
}

export function removeServiceCharge(): OrderingAction {
  return {
    type: "REMOVE_SERVICE_CHARGE",
  };
}

export function applyServiceCharge(): OrderingAction {
  return {
    type: "APPLY_SERVICE_CHARGE",
  };
}

export function setOrderDispatchTime(timeSlot: TimeSlot | null): ThunkAction {
  return async (dispatch) => {
    dispatch({
      type: "SET_DISPATCH_TIME",
      timeSlot,
    });
  };
}
